import React, { useState, useEffect } from "react";
import axios from "axios";
import { LuUserPlus, LuTrash2 } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";
import { compareFloors, floorLabel, distinctFloors } from "../shared/utils";
import { UnitFormModal, deleteTenant } from "./UnitFormModal";
import { UnitDetail } from "./UnitDetail";

export function UnitsRegister({ siteId, scopeType, scopeId, onCountChange }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [search, setSearch] = useState("");
  const [hoveredAdd, setHoveredAdd] = useState(false);
  const [hoveredBin, setHoveredBin] = useState(null);
  // "all" or one floor_level value ("" = units with no floor set).
  const [floorTab, setFloorTab] = useState("all");

  const loadUnits = () => {
    axios
      .get(`${API}/site-units/?${scopeType}=${scopeId}`)
      .then((res) => {
        const loaded = scopeType === "site"
          ? res.data.filter((u) => !u.area)
          : res.data;
        setUnits(loaded);
        onCountChange && onCountChange(loaded.length);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadUnits(); }, [scopeType, scopeId]);

  if (selectedUnit) {
    return (
      <UnitDetail
        unit={selectedUnit}
        siteId={siteId}
        onBack={() => { setSelectedUnit(null); loadUnits(); }}
      />
    );
  }

  // Floor tabs only earn their place on a multi-storey section.
  const floors = distinctFloors(units.map((u) => u.floor_level));
  const hasFloors = floors.length >= 2;
  const activeFloor = hasFloors && floors.includes(floorTab) ? floorTab : "all";

  // Floor first (bottom up), then the API's own order within a floor.
  const sortedUnits = hasFloors
    ? units
        .map((u, i) => [u, i])
        .sort(([a, ai], [b, bi]) => compareFloors(a.floor_level, b.floor_level) || ai - bi)
        .map(([u]) => u)
    : units;

  // A search always covers every floor, so a unit number is found wherever it is.
  const q = search.trim().toLowerCase();
  const filteredUnits = q
    ? sortedUnits.filter((u) =>
        (u.unit_number || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.floor_level || "").toLowerCase().includes(q)
      )
    : activeFloor === "all"
      ? sortedUnits
      : sortedUnits.filter((u) => (u.floor_level || "") === activeFloor);

  const gridCols = hasFloors ? "80px 110px 2fr 2fr 36px" : "80px 2fr 2fr 36px";

  const floorChipStyle = (isActive) => ({
    flex: "0 0 auto",
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: isActive ? "600" : "400",
    color: isActive ? colors.activeBlue : "#666",
    borderBottom: isActive ? `2px solid ${colors.activeBlue}` : "2px solid transparent",
    whiteSpace: "nowrap",
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search unit #, name or level..."
          style={{ ...inputStyle, maxWidth: "260px" }}
        />
        <button
          onClick={() => setShowAdd(true)}
          onMouseEnter={() => setHoveredAdd(true)}
          onMouseLeave={() => setHoveredAdd(false)}
          style={{
            background: hoveredAdd ? "#dbeafe" : "#f1f5f9",
            border: "none", borderRadius: "6px", padding: "8px 10px",
            cursor: "pointer", color: hoveredAdd ? colors.blue : "#64748b",
            display: "inline-flex", alignItems: "center", gap: "6px",
            fontSize: "12px", fontWeight: "600",
            transition: "background 0.15s, color 0.15s",
          }}>
          <LuUserPlus size={16} />
          Add Tenant
        </button>
      </div>
      {hasFloors && (
        <div style={{
          display: "flex", flexWrap: "wrap", alignItems: "center",
          borderBottom: `1px solid ${colors.border}`, marginBottom: "12px",
          opacity: q ? 0.5 : 1,
        }}>
          {["all", ...floors].map((f) => (
            <div key={f} onClick={() => { setFloorTab(f); setSearch(""); }} style={floorChipStyle(!q && activeFloor === f)}>
              {f === "all" ? "All" : floorLabel(f)}
            </div>
          ))}
        </div>
      )}
      <div style={styles.card}>
        <div style={{ ...styles.tableHeader, gridTemplateColumns: gridCols }}>
          <div>#</div>
          {hasFloors && <div>Level</div>}
          <div>Name</div>
          <div>Contact</div>
          <div></div>
        </div>
        {loading ? (
          <div style={styles.loading}>Loading...</div>
        ) : units.length === 0 ? (
          <div style={styles.loading}>No units yet. Add the first unit above.</div>
        ) : filteredUnits.length === 0 ? (
          <div style={styles.loading}>{q ? "No units match your search." : "No units on this level."}</div>
        ) : (
          filteredUnits.map((unit) => (
            <div
              key={unit.id}
              onClick={() => setSelectedUnit(unit)}
              style={{
                ...styles.tableRow(false),
                gridTemplateColumns: gridCols,
                cursor: "pointer",
              }}
            >
              <div style={{ fontWeight: "600", color: colors.slate }}>
                {unit.unit_number || "—"}
              </div>
              {hasFloors && (
                <div style={{ fontSize: "13px", color: "#64748b" }}>{unit.floor_level || "—"}</div>
              )}
              <div style={{ fontWeight: "500" }}>{unit.name || "—"}</div>
              <div style={{ fontSize: "13px" }}>
                {unit.contacts?.[0] ? (
                  <>
                    <div style={{ fontWeight: "500" }}>{unit.contacts[0].name}</div>
                    {unit.contacts[0].phone && <div style={{ color: "#999", fontSize: "12px" }}>{unit.contacts[0].phone}</div>}
                  </>
                ) : (
                  <span style={{ color: "#bbb" }}>No contact</span>
                )}
              </div>
              <div
                onClick={async (e) => {
                  e.stopPropagation();
                  if (await deleteTenant(unit)) loadUnits();
                }}
                onMouseEnter={() => setHoveredBin(unit.id)}
                onMouseLeave={() => setHoveredBin(null)}
                title="Delete tenant"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: hoveredBin === unit.id ? "#475569" : "#94a3b8",
                  cursor: "pointer",
                }}
              >
                <LuTrash2 size={15} />
              </div>
            </div>
          ))
        )}
        <div style={styles.count}>
          {q || activeFloor !== "all"
            ? `${filteredUnits.length} of ${units.length} units`
            : `${units.length} unit${units.length !== 1 ? "s" : ""}`}
        </div>
      </div>
      {showAdd && (
        <UnitFormModal
          siteId={siteId}
          areaId={scopeType === "area" ? scopeId : null}
          onClose={() => setShowAdd(false)}
          onSave={() => { setShowAdd(false); loadUnits(); }}
        />
      )}
    </div>
  );
}
